import { useState, useRef, useEffect } from 'react';
import config from '../config';
import { authFetch, describeApiError } from '../api/authFetch';

export const useFileUpload = ({ showMessage }) => {
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [stagedFiles, setStagedFiles] = useState([]); // Files waiting to be sent with message

  // Object URLs created to preview a staged file (see getStagedFilePreviewUrl
  // / Chat.js's handlePreviewStagedFile), keyed by staged file id. Created
  // lazily on first preview rather than eagerly for every staged file, and
  // revoked once that file's staged lifecycle ends - explicit removal
  // (removeStagedFile) or the whole batch clearing after send
  // (clearStagedFiles) - otherwise each preview leaks a blob URL for the
  // life of the tab.
  const previewUrlsRef = useRef(new Map());

  // removeStagedFile/clearStagedFiles cover the normal end of a staged
  // file's life, but a student can also navigate away from /chat entirely
  // (SPA route change, not a page reload) while files are still staged and
  // previewed - that unmounts this hook without ever calling either of
  // those, so anything still in previewUrlsRef would otherwise leak for the
  // rest of the tab's life. Mirrors useChatActions.js's
  // attachedFileObjectUrlsRef unmount cleanup.
  useEffect(() => {
    // Captured once, at mount - but it's the same Map instance
    // getStagedFilePreviewUrl/revokeStagedFilePreviewUrl/clearStagedFiles
    // mutate in place (never reassigned), so this still reflects whatever
    // is cached at unmount time, not a stale mount-time snapshot.
    const previewUrls = previewUrlsRef.current;
    return () => {
      previewUrls.forEach((url) => URL.revokeObjectURL(url));
      previewUrls.clear();
    };
  }, []);

  const handleFileUpload = async (file, settings = {}) => {
    // If staging mode, add to staged files without uploading
    if (settings.stage) {
      const stagedFile = {
        id: `staged_${Date.now()}_${Math.random()}`,
        name: file.name,
        size: file.size,
        type: file.type,
        file: file, // Keep original file for later upload
        status: 'staged'
      };
      
      setStagedFiles(prev => [...prev, stagedFile]);
      return stagedFile;
    }

    // Normal upload process
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('settings', JSON.stringify(settings));

      const response = await authFetch(`${config.API_BASE_URL}/api/upload`, {
        method: 'POST',
        body: formData,
      }, { timeoutMs: 60000 });

      if (response.status === 401) {
        window.location.href = '/signin';
        return;
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to upload file');
      }

      const result = await response.json();
      
      // Add the uploaded file to the uploaded files list
      const newFile = {
        id: result.id || Date.now(),
        name: file.name,
        size: file.size,
        type: file.type,
        url: result.url,
        uploadDate: new Date().toISOString(),
        status: 'uploaded'
      };

      setUploadedFiles(prev => [...prev, newFile]);

      if (!settings.silent) {
        showMessage("File uploaded successfully!", "success");
      }
      return newFile;

    } catch (error) {
      console.error('File upload failed:', error);
      if (!settings.silent) {
        showMessage(`Failed to upload file: ${describeApiError(error)}`, "error");
      }
      throw error;
    }
  };

  const uploadStagedFiles = async () => {
    if (stagedFiles.length === 0) return [];

    const uploadedResults = [];
    const failedFileNames = [];

    for (const stagedFile of stagedFiles) {
      try {
        const result = await handleFileUpload(stagedFile.file, { silent: true });
        uploadedResults.push(result);
      } catch (error) {
        console.error(`Failed to upload staged file ${stagedFile.name}:`, error);
        failedFileNames.push(stagedFile.name);
        // Continue with other files even if one fails
      }
    }

    // A student attaching several files shouldn't have one silently dropped
    // with no sign anything went wrong - call out which file(s) didn't make it.
    if (failedFileNames.length > 0 && showMessage) {
      const label = failedFileNames.length === 1 ? "this file" : "these files";
      showMessage(
        `We couldn't attach ${label}: ${failedFileNames.join(', ')}. The rest of your message was sent.`,
        "error"
      );
    }

    // Clear staged files after upload
    setStagedFiles([]);

    return uploadedResults;
  };

  // Lazily creates (and caches) an object URL for previewing a staged file,
  // so repeated preview clicks on the same file reuse one URL instead of
  // leaking a new one each time. Paired with revokeStagedFilePreviewUrl
  // below, called from removeStagedFile/clearStagedFiles once the file is
  // no longer staged.
  const getStagedFilePreviewUrl = (stagedFile) => {
    const existing = previewUrlsRef.current.get(stagedFile.id);
    if (existing) return existing;
    const url = URL.createObjectURL(stagedFile.file || stagedFile);
    previewUrlsRef.current.set(stagedFile.id, url);
    return url;
  };

  const revokeStagedFilePreviewUrl = (fileId) => {
    const url = previewUrlsRef.current.get(fileId);
    if (url) {
      URL.revokeObjectURL(url);
      previewUrlsRef.current.delete(fileId);
    }
  };

  const removeStagedFile = (fileId) => {
    revokeStagedFilePreviewUrl(fileId);
    setStagedFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const clearStagedFiles = () => {
    previewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    previewUrlsRef.current.clear();
    setStagedFiles([]);
  };

  const handleFileDelete = async (file) => {
    try {
      const response = await authFetch(`${config.API_BASE_URL}/api/files/${file.id}`, {
        method: 'DELETE',
      });

      if (response.status === 401) {
        window.location.href = '/signin';
        return;
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to delete file');
      }

      setUploadedFiles(prev => prev.filter(f => f.id !== file.id));
      showMessage("File deleted successfully!", "success");

    } catch (error) {
      console.error('File deletion failed:', error);
      showMessage(`Failed to delete file: ${describeApiError(error)}`, "error");
    }
  };

  const handleFileDownload = (file) => {
    // Create a download link
    const link = document.createElement('a');
    link.href = file.url;
    link.download = file.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return {
    uploadedFiles,
    setUploadedFiles,
    stagedFiles,
    setStagedFiles,
    handleFileUpload,
    uploadStagedFiles,
    removeStagedFile,
    clearStagedFiles,
    getStagedFilePreviewUrl,
    handleFileDelete,
    handleFileDownload,
    formatFileSize
  };
};
